import { Modal, Form, Select, Switch, InputNumber, Divider, Typography, Row, Col } from "antd";
import axios from "axios";
import { useEffect, useMemo, useState } from "react";

import { showErrorNotification, showSuccessNotification } from "../../../../utils/notification";

const { Text } = Typography;

const CourseModuleLinkFormModal = ({
  isOpenCourseModuleLinkFormModal,
  onCancel,
  onSuccess,
  getApiUrl,
  courseUniquePublicId,
  availableModules = [],
  linkedModules = [],
  activeLinkData = null
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const isEdit = !!activeLinkData;

  const prerequisiteOptions = useMemo(() => {
    if (!isEdit) {
      return linkedModules;
    }
    return linkedModules.filter(
      (module) => module.uniquePublicId !== activeLinkData.uniquePublicId
    );
  }, [linkedModules, activeLinkData, isEdit]);

  const selectableModules = useMemo(() => {
    if (isEdit) {
      return [];
    }
    const linkedIds = new Set(linkedModules.map((module) => module.uniquePublicId));
    return availableModules.filter((module) => !linkedIds.has(module.uniquePublicId));
  }, [availableModules, linkedModules, isEdit]);

  useEffect(() => {
    if (!isOpenCourseModuleLinkFormModal) {
      return;
    }

    if (isEdit) {
      const {
        displayOrder,
        settings: { isVisible, isPreviewAllowed },
        previousModuleUniquePublicId,
        nextModuleUniquePublicId
      } = activeLinkData;
      form.setFieldsValue({
        displayOrder,
        isVisible,
        isPreviewAllowed,
        previousModuleUniquePublicId,
        nextModuleUniquePublicId
      });
    } else {
      form.resetFields();
    }
  }, [isOpenCourseModuleLinkFormModal, isEdit, activeLinkData, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      await axios.post(getApiUrl("/link-module"), {
        courseUniquePublicId,
        moduleUniquePublicId: isEdit ? activeLinkData.uniquePublicId : values.moduleUniquePublicId,
        displayOrder: values.displayOrder,
        previousModuleUniquePublicId: values.previousModuleUniquePublicId || null,
        nextModuleUniquePublicId: values.nextModuleUniquePublicId || null,
        settings: {
          isVisible: values.isVisible,
          isPreviewAllowed: values.isPreviewAllowed
        }
      });
      showSuccessNotification(isEdit ? "Module link updated" : "Module linked");
      onSuccess();
    } catch (err) {
      showErrorNotification(err);
    } finally {
      setLoading(false);
    }
  };

  const { Item } = Form;
  const { Option } = Select;

  return (
    <Modal
      open={isOpenCourseModuleLinkFormModal}
      onCancel={onCancel}
      onOk={handleSubmit}
      confirmLoading={loading}
      width={640}
      title={isEdit ? "Edit Module Link" : "Link Module to Course"}
      style={{
        overflowY: "auto",
        overflowX: "hidden"
      }}
    >
      <Form
        layout="vertical"
        form={form}
        initialValues={{
          isVisible: true,
          isPreviewAllowed: false
        }}
      >
        {!isEdit && (
          <>
            <Text strong>Module</Text>
            <Item name="moduleUniquePublicId" rules={[{ required: true }]} style={{ marginTop: 8 }}>
              <Select placeholder="Select module to link" showSearch optionFilterProp="children">
                {selectableModules.map((module) => (
                  <Option key={module.uniquePublicId} value={module.uniquePublicId}>
                    {module.title}
                  </Option>
                ))}
              </Select>
            </Item>
            <Divider />
          </>
        )}

        <Text strong>Ordering</Text>
        <Row gutter={16} style={{ marginTop: 8 }}>
          <Col xs={24} sm={12}>
            <Item label="Display Order" name="displayOrder">
              <InputNumber min={1} style={{ width: "100%" }} placeholder="Auto if empty" />
            </Item>
          </Col>
        </Row>

        <Divider />

        <Text strong>Module Flow</Text>
        <Row gutter={[16, 16]} style={{ marginTop: 8 }}>
          <Col xs={24} sm={12}>
            <Item label="Previous Module" name="previousModuleUniquePublicId">
              <Select allowClear placeholder="None">
                {prerequisiteOptions.map((module) => (
                  <Option key={module.uniquePublicId} value={module.uniquePublicId}>
                    {module.title}
                  </Option>
                ))}
              </Select>
            </Item>
          </Col>

          <Col xs={24} sm={12}>
            <Item label="Next Module" name="nextModuleUniquePublicId">
              <Select allowClear placeholder="None">
                {prerequisiteOptions.map((module) => (
                  <Option key={module.uniquePublicId} value={module.uniquePublicId}>
                    {module.title}
                  </Option>
                ))}
              </Select>
            </Item>
          </Col>
        </Row>

        <Divider />

        <Text strong>Visibility & Access</Text>

        <div style={{ marginTop: 12, display: "flex", gap: 32, flexWrap: "wrap" }}>
          <Item
            name="isVisible"
            valuePropName="checked"
            style={{ marginBottom: 12 }}
            label="Visibility"
          >
            <Switch unCheckedChildren="Hidden" checkedChildren="Visible" />
          </Item>

          <Item
            name="isPreviewAllowed"
            valuePropName="checked"
            style={{ marginBottom: 0 }}
            label="Course Preview"
          >
            <Switch unCheckedChildren="Disabled" checkedChildren="Enabled" />
          </Item>
        </div>
      </Form>
    </Modal>
  );
};

export default CourseModuleLinkFormModal;
